import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Clipboard, 
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import ProfileIconLink from "@/components/ProfileIconLink";
import { listClientAssessments, type ClientAssessment } from "@/integrations/backend/assessments";

interface AssessmentsViewProps {
  onBack: () => void;
}

const AssessmentsView = ({ onBack }: AssessmentsViewProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [assessments, setAssessments] = useState<ClientAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const rows = await listClientAssessments();
        if (!active) return;
        setAssessments(rows);
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e.message : "Failed to load assessments");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen gradient-dark">
      {/* Header */}
      <div className={`bg-gradient-to-r from-dark-bg to-dark-card text-white p-6 pt-8 pb-10 rounded-b-[2.5rem] border-b-2 border-primary/20 ${!isMobile ? "md:ml-16" : ""}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Button 
              variant="ghost" 
              className="bg-white/10 rounded-full h-9 px-3 flex items-center justify-center hover:bg-white/20"
              onClick={onBack}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm">Back to Explore</span>
            </Button>
          </div>
          <ProfileIconLink />
        </div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clipboard className="w-6 h-6 text-primary" />
            <span>Assessments</span>
          </h1>
        </div>
        
        {/* Assessment Stats */}
        <div className="bg-dark-slate rounded-2xl p-4 border border-primary/20">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{loading ? "…" : assessments.length}</div>
            <div className="text-xs text-gray-light">Total</div>
          </div>
        </div>
      </div>

      <div className={`p-6 space-y-6 ${isMobile ? "pb-24" : ""} ${!isMobile ? "md:ml-16" : ""}`}>
        {error && (
          <Card className="border border-primary/20 shadow-card bg-dark-card">
            <CardContent className="p-4 text-sm text-red-200">
              Failed to load assessments: {error}
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card className="border border-primary/20 shadow-card bg-dark-card">
            <CardContent className="p-8 text-center">
              <Clipboard className="w-12 h-12 text-gray-light mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Loading assessments…</h3>
              <p className="text-gray-light">Just a moment.</p>
            </CardContent>
          </Card>
        ) : assessments.length === 0 ? (
          <Card className="border border-primary/20 shadow-card bg-dark-card">
            <CardContent className="p-8 text-center">
              <Clipboard className="w-12 h-12 text-gray-light mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No assessments yet</h3>
              <p className="text-gray-light">Your coach hasn’t added any assessments.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {assessments.map((assessment) => (
              <Card key={assessment.id} className="border border-primary/20 shadow-card bg-dark-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base sm:text-lg">{assessment.name}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {assessment.description ? (
                    <p className="text-gray-light text-sm mb-4">{assessment.description}</p>
                  ) : null}

                  <div className="flex flex-col sm:flex-row gap-2">
                    {assessment.typeform_url ? (
                      <Button
                        className="w-full sm:w-auto gradient-primary text-black hover:opacity-90"
                        onClick={() => navigate(`/assessments/${assessment.id}`)}
                      >
                        Open Assessment
                      </Button>
                    ) : (
                      <Button disabled className="w-full sm:w-auto">
                        Coming soon
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentsView; 